import * as React from 'react';
import { t } from '@moovly/i18n';
import { IconEdit, IconTrash } from '@moovly/ui';
import { Library, LibraryShare } from '@moovly/clients/lib/resources/libraries/libraries.interfaces';
import {
  Table,
  TableAction,
  TableBody,
  TableCol,
  TableHeadCol,
  TableHeader,
  TableLoader,
  TableRow,
} from '../../../components/Table';
import useCurrentUser from '../../../hooks/useCurrentUser';
import { GroupInfo } from '@moovly/clients/lib/resources/group/group.interfaces';

interface LibrariesTableProps {
  libraries: Library[] | null;
  isLoading: boolean;
  onEdit: (library: Library) => void;
  onDelete: (library: Library) => void;
}

const compareLibraries = (a: Library, b: Library) => {
  if (!!a.shares?.length && !!b.shares?.length)
    return a.name.toLocaleLowerCase() >= b.name.toLocaleLowerCase() ? 1 : -1;
  if (!!a.shares?.length) return 1;
  return -1;
};
const getShareType = (shares: LibraryShare[], groups: GroupInfo[]) => {
  let type = 'user';
  const params: any = {};
  if (!shares || !shares.length) {
    type = 'user';
  } else if (shares.some((s) => s.type === 'organization')) {
    type = 'organization';
  } else if (shares.some((s) => s.type === 'group')) {
    if (groups.length > 1) {
      type = 'groupWithName';
      params.groupName = shares
        .filter((s) => s.type === 'group')
        .map((share) => {
          const group = groups.find((g) => g.id === share.id);
          return group && group.name && `"${group.name}"`;
        })
        .filter((g) => !!g)
        .join(', ');
    } else {
      type = 'group';
    }
  }
  return t(`libraries.shareType.${type}`, params);
};

const LibrariesTable: React.FC<LibrariesTableProps> = ({ libraries, isLoading = false, onEdit, onDelete }) => {
  const { groupsWithAdminRight } = useCurrentUser();
  const handleEdit = React.useCallback((lib: Library) => () => onEdit(lib), [onEdit]);
  const handleDelete = React.useCallback((lib: Library) => () => onDelete(lib), [onDelete]);

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHeadCol>{t('libraries.table.name')}</TableHeadCol>
            <TableHeadCol>{t('libraries.table.shareType')}</TableHeadCol>
            <TableHeadCol>{t('libraries.table.actions')}</TableHeadCol>
          </TableRow>
        </TableHeader>
        <TableBody>
          {libraries &&
            [...libraries].sort(compareLibraries).map((library) => (
              <TableRow key={`library-table-item-${library.id}`}>
                <TableCol>{library.name}</TableCol>
                <TableCol>{getShareType(library.shares || [], groupsWithAdminRight)}</TableCol>
                <TableCol>
                  <div className="d-flex flex-row">
                    <TableAction onClick={handleEdit(library)} icon={IconEdit} />
                    <TableAction onClick={handleDelete(library)} icon={IconTrash} />
                  </div>
                </TableCol>
              </TableRow>
            ))}
          {isLoading && <TableLoader columnns={3} rows={3} />}
        </TableBody>
      </Table>
    </>
  );
};

export default LibrariesTable;
